import React, { useRef } from 'react';
import { Palette, Link2, Video, Lightbulb } from 'lucide-react';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * Éditeur Markdown simple avec barre d'outils
 * 0 dépendance externe - Pure React
 * 
 * Supporte: Titres (H1-H3), Gras, Italique, Souligné, Couleur marron, Listes
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = 'Écrivez votre article ici...'
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  /**
   * Insère la syntaxe Markdown autour du texte sélectionné
   */
  const insertMarkdown = (before: string, after: string = '', newLine: boolean = false) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let newValue: string;
    let newCursorPos: number;
    
    if (newLine) {
      // Pour les titres: ajouter sur une nouvelle ligne
      const beforeCursor = value.substring(0, start);
      const afterCursor = value.substring(end);
      
      // Trouver le début de la ligne actuelle
      const lastNewLine = beforeCursor.lastIndexOf('\n');
      const lineStart = lastNewLine === -1 ? 0 : lastNewLine + 1;
      const currentLine = value.substring(lineStart, end);
      
      if (currentLine.trim()) {
        // Si il y a du texte, l'entourer avec la syntaxe
        newValue = beforeCursor + before + selectedText + after + afterCursor;
        newCursorPos = start + before.length + selectedText.length;
      } else {
        // Sinon, juste insérer la syntaxe
        newValue = beforeCursor + before + afterCursor;
        newCursorPos = start + before.length;
      }
    } else {
      // Pour le formatage inline: entourer le texte sélectionné
      newValue = 
        value.substring(0, start) +
        before + selectedText + after +
        value.substring(end);
      newCursorPos = start + before.length + selectedText.length;
    }
    
    onChange(newValue);
    
    // Repositionner le curseur
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };
  
  /**
   * Insère un lien avec prompt pour l'URL
   */
  const insertLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    const linkText = selectedText || 'texte du lien';
    const url = prompt('Entrez l\'URL du lien :', 'https://');
    
    if (url) {
      const linkMarkdown = `[${linkText}](${url})`;
      const newValue = value.substring(0, start) + linkMarkdown + value.substring(end);
      onChange(newValue);
      
      setTimeout(() => {
        textarea.focus();
        const newPos = start + linkMarkdown.length;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    }
  };
  
  /**
   * Insère une vidéo YouTube avec prompt pour l'ID
   */
  const insertYouTube = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const input = prompt(
      'Entrez l\'ID de la vidéo YouTube\n\n' +
      'Exemples:\n' +
      '• ID: dQw4w9WgXcQ\n' +
      '• URL complète: https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      ''
    );
    
    if (input) {
      // Extraire l'ID si c'est une URL complète
      let videoId = input.trim();
      const urlMatch = input.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (urlMatch) {
        videoId = urlMatch[1];
      }
      
      const youtubeMarkdown = `{youtube}${videoId}{/youtube}`;
      const newValue = value.substring(0, start) + '\n\n' + youtubeMarkdown + '\n\n' + value.substring(end);
      onChange(newValue);
      
      setTimeout(() => {
        textarea.focus();
        const newPos = start + youtubeMarkdown.length + 4;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    }
  };
  
  return (
    <div className="space-y-2">
      {/* Barre d'outils */}
      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-300">
        {/* Titres */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => insertMarkdown('# ', '', true)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-semibold"
            title="Titre principal (H1)"
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('## ', '', true)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-semibold"
            title="Sous-titre (H2)"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('### ', '', true)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-semibold"
            title="Petit titre (H3)"
          >
            H3
          </button>
        </div>
        
        <div className="w-px bg-gray-400 self-stretch" />
        
        {/* Formatage inline */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => insertMarkdown('**', '**')}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-bold"
            title="Gras"
          >
            Gras
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('*', '*')}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm italic"
            title="Italique"
          >
            Italique
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('~~', '~~')}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm underline"
            title="Souligné"
          >
            Souligné
          </button>
        </div>
        
        <div className="w-px bg-gray-400 self-stretch" />
        
        {/* Couleur et liste */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => insertMarkdown('{', '}')}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-medium flex items-center gap-1"
            style={{ color: '#8B4513' }}
            title="Texte en marron (emphase)"
          >
            <Palette className="w-4 h-4" />
            <span>Marron</span>
          </button>
          <button
            type="button"
            onClick={() => insertMarkdown('- ', '', true)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm"
            title="Liste à puces"
          >
            • Liste
          </button>
        </div>
        
        <div className="w-px bg-gray-400 self-stretch" />
        
        {/* Liens et médias */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={insertLink}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-medium flex items-center gap-1"
            title="Insérer un lien"
          >
            <Link2 className="w-4 h-4" />
            <span>Lien</span>
          </button>
          <button
            type="button"
            onClick={insertYouTube}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-medium flex items-center gap-1 bg-red-50 hover:bg-red-100"
            title="Insérer une vidéo YouTube"
          >
            <Video className="w-4 h-4" />
            <span>YouTube</span>
          </button>
        </div>
      </div>
      
      {/* Zone de texte */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-96 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-y"
        style={{ minHeight: '300px' }}
      />
      
      {/* Aide rapide */}
      <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
        <div className="flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <strong className="text-blue-900">Aide rapide :</strong>
            <span className="ml-2">Sélectionnez du texte et cliquez sur un bouton, ou tapez directement :</span>
            <div className="mt-1 flex flex-wrap gap-2">
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300">**gras**</code>
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300">*italique*</code>
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300">~~souligné~~</code>
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300">{'{'}marron{'}'}</code>
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300"># Titre</code>
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300">- Liste</code>
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300">[texte](url)</code>
              <code className="bg-white px-2 py-0.5 rounded border border-blue-300">{'{'}youtube{'}'}ID{'{/'}youtube{'}'}</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
